import { useRef, useState } from "react";
import { useFormDataStore } from "../store/formDataStore";
import { EforceIncidentModel } from "../../../shared/interfaces/EforceModels";

/**
 * Rejection reason used when a pending save is intentionally cancelled because
 * the editing socket was torn down (the officer navigated away from the edit
 * page, or the socket reconnected). Awaiters (submit / generate-full) treat
 * this as a silent abort rather than a real save failure — the server's ack can
 * never arrive on a closed socket, so surfacing a "could not be saved" toast
 * would be misleading.
 */
export class SaveCancelledError extends Error {
  constructor() {
    super("Save cancelled: navigated away from the edit page");
    this.name = "SaveCancelledError";
  }
}

interface UseReportSavingProps {
  liveWebSocket: React.MutableRefObject<WebSocket | null>;
  showError: (message: string) => void;
}

export const useReportSaving = ({
  liveWebSocket,
  showError,
}: UseReportSavingProps) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const SAVE_TIMEOUT_MS = 15000;

  const saveDeferredRef = useRef<{
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
    timeoutId: NodeJS.Timeout | null;
  } | null>(null);

  const handleSaveChanges = async (
    exit = false,
    timeout = false,
    payloadOverride?: Partial<EforceIncidentModel>,
  ) => {
    const { formData: latestFormData } = useFormDataStore.getState();
    const dataToSend = payloadOverride ?? latestFormData;
    if (!dataToSend) {
      showError("Failed to save because no report data is loaded.");
      return;
    }

    // Null ref means the connect is still starting up, or the editor was torn
    // down (navigation/unmount). Neither is a real connection loss — return
    // silently (no deferred is created, so callers' `!saveDeferredRef.current`
    // guards reset isSaving) instead of showing a spurious toast. A genuinely
    // dropped socket leaves the object present with readyState !== OPEN.
    if (!liveWebSocket.current) return;

    if (liveWebSocket.current.readyState !== WebSocket.OPEN) {
      showError(
        "Connection to server lost. Please refresh the page to make edits."
      );
      return;
    }

    let resolve: () => void;
    let reject: (error: Error) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    // Guard against an "unhandled rejection" warning when this deferred is
    // cancelled (or times out) while no caller is awaiting it — handleSaveAndExit
    // / handleInstantSave / handleTimeoutSave fire-and-forget and only read
    // saveDeferredRef to reset isSaving. The extra no-op handler does not stop
    // `await promise` from throwing at the call sites that do await it.
    void promise.catch(() => {});

    const timeoutId = setTimeout(() => {
      setIsSaving(false);
      reject(new Error("Save operation timed out after 15 seconds"));
    }, SAVE_TIMEOUT_MS);

    saveDeferredRef.current = {
      promise,
      resolve: resolve!,
      reject: reject!,
      timeoutId,
    };

    liveWebSocket.current.send(
      JSON.stringify({
        action: "save",
        updatedIncident: dataToSend,
        exit,
        timeout,
      })
    );
  };

  const handleSaveAndExit = async () => {
    setIsSaving(true);
    await handleSaveChanges(true);
    if (!saveDeferredRef.current) {
      setIsSaving(false);
    }
  };

  const handleTimeoutSave = async () => {
    setIsSaving(true);
    await handleSaveChanges(true, true);
    if (!saveDeferredRef.current) {
      setIsSaving(false);
    }
  };

  const handleInstantSave = async () => {
    setIsSaving(true);
    await handleSaveChanges(false);
    if (!saveDeferredRef.current) {
      setIsSaving(false);
    }
  };
  
  // Also expose handleSaveChanges for generic use if needed.
  // Accepts an optional payload override (e.g. narrative-only submit sends
  // only General Information + Narratives instead of the full form state).
  const handleSaveCurrentChanges = async (
    payloadOverride?: Partial<EforceIncidentModel>,
  ) => {
    await handleSaveChanges(false, false, payloadOverride);
  };

  return {
    isSaving,
    setIsSaving,
    saveDeferredRef,
    handleSaveAndExit,
    handleTimeoutSave,
    handleInstantSave,
    handleSaveCurrentChanges,
  };
};
